import { TZDate } from '@date-fns/tz'
import { addDays, endOfDay, endOfMonth, startOfDay } from 'date-fns'

import { toCalendarDate } from '@/services/stats/statsRange'
import type { CurrencyAmount } from './lineItemsApi'

/**
 * One period of a budget, as the API models it: a calendar span in the
 * network's time zone with an amount to spend inside it. A line item (or an
 * order) either has a single `objectives.BUDGET` or a list of these — never
 * both, which the backend rejects.
 *
 * The periods are one unbroken timeline: each one starts the day after the
 * previous one ends, and the object's own start and end dates are the first
 * period's start and the last period's end.
 */
export interface BudgetPeriod {
  /** `yyyy-MM-dd` in the network's time zone. */
  startDay: string
  endDay: string
  amount?: CurrencyAmount
  description?: string
  /** Spent in this period so far. Server-derived, read-only. */
  spend?: CurrencyAmount
  /** The amount plus whatever rolled over from the period before. Read-only. */
  adjustedAmount?: CurrencyAmount
  /** Spend that predates the first period, when it is counted into it. Read-only. */
  priorSpend?: CurrencyAmount
}

/** Reads the budget list off an API response, dropping anything malformed. */
export function mapBudgetPeriods(value: unknown): BudgetPeriod[] {
  if (!Array.isArray(value)) return []
  return (value as Record<string, unknown>[])
    .filter((b) => typeof b.startDay === 'string' && typeof b.endDay === 'string')
    .map((b) => ({
      startDay: b.startDay as string,
      endDay: b.endDay as string,
      amount: b.amount as CurrencyAmount | undefined,
      description: b.description as string | undefined,
      spend: b.spend as CurrencyAmount | undefined,
      adjustedAmount: b.adjustedAmount as CurrencyAmount | undefined,
      priorSpend: b.priorSpend as CurrencyAmount | undefined,
    }))
    .sort((a, b) => a.startDay.localeCompare(b.startDay))
}

/** What goes back to the API — the derived figures are the server's own. */
export function budgetPeriodsForSave(periods: BudgetPeriod[], currency: string): BudgetPeriod[] {
  return periods.map((p) => ({
    startDay: p.startDay,
    endDay: p.endDay,
    amount: { amount: p.amount?.amount ?? 0, currency: p.amount?.currency || currency },
    ...(p.description ? { description: p.description } : {}),
  }))
}

/** A `yyyy-MM-dd` as midnight of that day in `timeZone`. */
function dayIn(day: string, timeZone: string): TZDate {
  const [year, month, date] = day.split('-').map(Number)
  return new TZDate(year, month - 1, date, timeZone)
}

/** Today's calendar date in the network's time zone. */
export function today(timeZone: string): string {
  return toCalendarDate(new TZDate(new Date(), timeZone))
}

/** The day `count` days after `day`, in the network's calendar. */
export function shiftDay(day: string, count: number, timeZone: string): string {
  return toCalendarDate(addDays(dayIn(day, timeZone), count))
}

/**
 * Whether a period can still be edited. The backend freezes a period once it
 * has ended — its amount is then immutable and it cannot be removed.
 */
export function isPeriodEditable(period: BudgetPeriod, timeZone: string): boolean {
  return period.endDay >= today(timeZone)
}

/** The period the network's clock is inside right now, if any. */
export function isCurrentPeriod(period: BudgetPeriod, timeZone: string): boolean {
  const now = today(timeZone)
  return period.startDay <= now && period.endDay >= now
}

/**
 * The period to append after `last` — the rest of the month it starts in, or
 * the rest of the current month when that would already be over.
 */
export function nextPeriod(last: BudgetPeriod | undefined, timeZone: string): BudgetPeriod {
  const now = today(timeZone)
  const startDay = last ? shiftDay(last.endDay, 1, timeZone) : now
  const monthEnd = toCalendarDate(endOfMonth(dayIn(startDay, timeZone)))
  // No amount: a fresh period is for the person to fill in, and an empty
  // field says that better than a zero they have to clear first.
  return {
    startDay,
    endDay: monthEnd < now ? toCalendarDate(endOfMonth(dayIn(now, timeZone))) : monthEnd,
  }
}

/**
 * Re-links the timeline after a period's end day moved or a period was
 * removed: every period starts the day after the one before it, and none ends
 * before it starts. Contiguity is what the backend demands, so the editor
 * keeps it by construction rather than reporting it as an error afterwards.
 */
export function relinkPeriods(periods: BudgetPeriod[], timeZone: string): BudgetPeriod[] {
  const linked: BudgetPeriod[] = []
  for (const period of periods) {
    const previous = linked[linked.length - 1]
    const startDay = previous ? shiftDay(previous.endDay, 1, timeZone) : period.startDay
    linked.push({
      ...period,
      startDay,
      endDay: period.endDay < startDay ? startDay : period.endDay,
    })
  }
  return linked
}

/**
 * The dates the line item must carry for these periods: the first period's
 * start and the last one's end, as instants in the network's time zone. The
 * backend rejects any other pair (`LINE_ITEM_DATES_MUST_ALIGN_WITH_BUDGET`),
 * so the dates are derived rather than edited.
 */
export function budgetFlight(
  periods: BudgetPeriod[],
  timeZone: string
): { startDate: string; endDate: string } | null {
  if (periods.length === 0) return null
  const start = startOfDay(dayIn(periods[0].startDay, timeZone))
  const end = endOfDay(dayIn(periods[periods.length - 1].endDay, timeZone))
  return {
    startDate: new Date(start.getTime()).toISOString(),
    endDate: new Date(end.getTime()).toISOString(),
  }
}

/** The day an instant falls on in the network's calendar. */
export function calendarDayOf(iso: string | undefined, timeZone: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return toCalendarDate(new TZDate(date, timeZone))
}

/** Everything the periods add up to, in their shared currency. */
export function budgetTotal(periods: BudgetPeriod[]): CurrencyAmount | undefined {
  if (periods.length === 0) return undefined
  return {
    amount: periods.reduce((sum, p) => sum + (p.amount?.amount ?? 0), 0),
    currency: periods.find((p) => p.amount?.currency)?.amount?.currency ?? '',
  }
}
